import { Injectable } from '@angular/core';

import { FirestorePathsService } from './firestorePaths.service';
import { FirestoreService } from './firestore.service';
// import { MapHelperService } from './mapHelper.service';
import { UtilsService } from './utils.service';
import { DateService } from './date.service';
import type {
    TDistance, TShuttle, TShuttleStatus, TDistinctColorInfo, TCoordinate, TVector
} from '@shared/models';
import { Constants } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class ShuttlesService {
    constructor(private firestoreService: FirestoreService, private firestorePathsService: FirestorePathsService,
        private utilsService: UtilsService, private dateService: DateService) {
    }

    public getNullableShuttle(): TShuttle {
        // const nullCoordinate: TCoordinate = { 'latitude': 0, 'longitude': 0 };
        // const nullDistance: TDistance = { 'kilometers': 0, 'miles': 0 };

        const shuttle: TShuttle = {
            shuttleName: '',
            status: 'Idle',
            passengerCapacity: 0,
            uid: '',
            description: '',
            driverName: '',
            color: '',
            seatsAvailable: 0,
            vector: this.getNullableVector()
        };
        return shuttle;
    }

    public getNullableVector(): TVector {
        const nullCoordinate: TCoordinate = { 'latitude': 0, 'longitude': 0 };
        const nullDistance: TDistance = { 'kilometers': 0, 'miles': 0 };

        const vector: TVector = {
            accuracy: '',
            currentLocation: nullCoordinate,
            currentTime: this.dateService.getMinimumDate(),
            distanceFromDealership: nullDistance,
            distanceToTicket: nullDistance,
            heading: '',
            modifiedDate: this.dateService.getMinimumDate(),
            nearAddress: '',
            previousLocation: nullCoordinate,
            previousTime: this.dateService.getMinimumDate(),
            speed: {
                'kph': 0,
                'mph': 0
            },
            tripUid: '',
            uid: '',
        }
        return vector;
    }

    public getObservable() {
        const path = this.firestorePathsService.getShuttlesPath();
        const observable = this.firestoreService.getObservable(path);
        return observable;
    }

    public async getByUid(uid: string): Promise<any> {
        const path = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.getByUid(path, uid);
    }

    public async getByFieldValue(fieldName: string, fieldValue: string): Promise<TShuttle[]> {
        const path = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.getByFieldValue(path, fieldName, fieldValue) as Promise<TShuttle[]>;
    }

    public async getAll(): Promise<TShuttle[]> {
        const path = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.getAll(path) as Promise<TShuttle[]>;
    }

    private async add(shuttle: TShuttle): Promise<TShuttle> {
        const path = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.add(path, shuttle) as Promise<TShuttle>;
    }

    // private async set(shuttle: TShuttle): Promise<TShuttle> {
    //     const path = this.firestorePathsService.getShuttlesPath();
    //     return this.firestoreService.set(path, shuttle) as Promise<TShuttle>;
    // }

    public async update(shuttle: TShuttle): Promise<TShuttle> {
        const path = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.update(path, shuttle) as Promise<TShuttle>;
    }

    public async save(shuttle: TShuttle): Promise<TShuttle> {
        if (!shuttle.color) {
            const numShuttles = await this.size();
            const colorInfo: TDistinctColorInfo = this.utilsService.getColorInfo(numShuttles);
            shuttle.color = colorInfo.name;
        }

        if (!shuttle.uid) {
            return this.add(shuttle);
        } else {
            return this.update(shuttle);
        }
    }

    public async delete(uid: string): Promise<void> {
        const collection = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.delete(collection, uid);
    }

    public async size(): Promise<number> {
        const collection = this.firestorePathsService.getShuttlesPath();
        return this.firestoreService.size(collection);
    }

    public async isShuttleNameUnique(shuttle: TShuttle): Promise<boolean> {
        let isUnique: boolean;

        const allShuttles = await this.getAll();
        const duplicateShuttles = allShuttles.filter((s) => (s.shuttleName === shuttle.shuttleName));

        if (shuttle.uid) {
            isUnique = (duplicateShuttles.length < 2);
        } else {
            isUnique = (duplicateShuttles.length === 0);
        }
        // const shuttles: TShuttle[] = await this.getByFieldValue('shuttleName', shuttle.shuttleName);
        // if (shuttle.uid) {
        //     isUnique = (shuttles && shuttles.length === 1);
        // } else {
        //     isUnique = (shuttles && shuttles.length === 0);
        // }
        return isUnique;
    }

    public async setAllShuttlesToIdle() {
        const promises: Promise<TShuttle>[] = [];

        const shuttles = await this.getAll();
        shuttles.forEach((shuttle) => {
            const p = this.updateShuttleToIdle(shuttle.uid!);
            promises.push(p);
        });
        await Promise.all(promises);
    }

    public async updateShuttleToIdle(shuttleUid: string) {
        return this.updateShuttleStatus(shuttleUid, 'Idle');
    }

    public async updateShuttleToAssigned(shuttleUid: string): Promise<any> {
        return this.updateShuttleStatus(shuttleUid, 'Assigned');
    }

    public async updateShuttleToTraveling(shuttleUid: string): Promise<any> {
        return this.updateShuttleStatus(shuttleUid, 'Traveling');
    }

    // public updateShuttleStatus(shuttleUid: string, status: TShuttleStatus): Promise<{}> {
    //     const promise = new Promise((resolve, reject) => {
    //         this.getByUid(shuttleUid)
    //         .then((shuttle: TShuttle) => {
    //             if (shuttle) {
    //                 shuttle.status = status;
    //                 this.update(shuttle);
    //                 resolve();
    //             } else {
    //                 reject();
    //             }
    //         })
    //         .catch (err => {
    //             reject(err);
    //         });
    //     });

    //     return promise;
    // }

    public async updateShuttleStatus(shuttleUid: string, status: TShuttleStatus): Promise<any> {
        const shuttle = await this.getByUid(shuttleUid);
        if (shuttle) {
            shuttle.status = status;
            await this.update(shuttle);
            return shuttle;
        }
        return Promise.resolve();
    }

    public async getIdleShuttles(): Promise<TShuttle[]> {
        return this.getShuttlesByStatus('Idle');
    }

    private async getShuttlesByStatus(status: TShuttleStatus): Promise<TShuttle[]> {
        const collection = this.firestorePathsService.getShuttlesPath();
        const shuttles = await this.firestoreService.getByFieldValue(collection, 'status', status) as TShuttle[];
        const sortedShuttles = this.sort(shuttles);
        return sortedShuttles;
    }

    public async getVectors(shuttles?: TShuttle[]): Promise<TVector[]> {
        const shuttlez = (shuttles) ? shuttles : await this.getAll();
        const vectors = shuttlez.map((shuttle) => shuttle.vector);

        return vectors;
    }

    public sort(shuttles: TShuttle[]) {
        shuttles.sort((a, b) => {
            if (a.shuttleName < b.shuttleName) {
                return -1;
            } else if (a.shuttleName > b.shuttleName) {
                return 1;
            } else {
                return 0;
            }
        });
        return shuttles;
    }

    public sortByDistance(shuttles: TShuttle[]) {
        const vectors = shuttles.map((shuttle) => shuttle.vector);
        vectors.sort((a, b) => {
            if (a.distanceFromDealership.miles < b.distanceFromDealership.miles) {
                return -1;
            } else if (a.distanceFromDealership.miles > b.distanceFromDealership.miles) {
                return 1;
            } else {
                return 0;
            }
        });
        return vectors;
    }

    public resetVector(shuttle: TShuttle): TShuttle {
        shuttle.vector = {
            accuracy: '',
            currentLocation: Constants.NULL_COORDINATE,
            currentTime: this.dateService.getMinimumDate(),
            distanceFromDealership: Constants.NULL_DISTANCE,
            distanceToTicket: Constants.NULL_DISTANCE,
            heading: '',
            modifiedDate: this.dateService.getMinimumDate(),
            nearAddress: '',
            previousLocation: Constants.NULL_COORDINATE,
            previousTime: this.dateService.getMinimumDate(),
            speed: {
                'kph': 0,
                'mph': 0
            },
            tripUid: '',
            uid: '',
        };

        return shuttle;
    }

    //     public async ensureAllVectorsExist(shuttles: TShuttle[]): Promise < void> {
    //     const promises: Promise<TVector>[] = [];

    //     shuttles.forEach((shuttle) => {
    //         const p = this.ensureVectorExists(shuttle);
    //         promises.push(p);
    //     });

    //     await Promise.all(promises);
    // }

    //     private supplyVectorDefaults(vector: TVector): void {
    //     vector.currentLocation = {} as ICoordinate;
    //     vector.previousLocation = {} as ICoordinate;
    //     vector.speed = { 'mph': 0, 'kph': 0 };
    //     vector.distanceFromDealership = { 'miles': 0, 'kilometers': 0 };
    //     this.dealershipcoordinate = this.sessionService.getDealership().address.coordinate;

    //     if(this.dealershipcoordinate) {
    //     vector.currentLocation.latitude = this.dealershipcoordinate.latitude;
    //     vector.currentLocation.longitude = this.dealershipcoordinate.longitude;
    //     vector.previousLocation.latitude = this.dealershipcoordinate.latitude;
    //     vector.previousLocation.longitude = this.dealershipcoordinate.longitude;
    //     vector.distanceFromDealership = {
    //         'kilometers': 0.0,
    //         'miles': 0.0
    //     };
    // } else {
    //     vector.currentLocation = Constants.NULL_COORDINATE;
    //     vector.previousLocation = Constants.NULL_COORDINATE;
    // }

    //     // vector.driver = {'fullName': 'No Driver', 'uid': 'na'};
    // }
}
